import React from "react";
import { Box } from "@mui/material";
import { CardHeading, formatDate, formatNumber } from "./MediaKitComponents";
import { catalogEngagementPercentFromConsumption } from "./MediaKitCatalogConsumptionCards";
import {
  catalogThumbSrc,
  type MediaKitYoutubeCatalogVideo,
} from "./MediaKitYoutubeCatalogList";
import { useMediaKitMetricsWindow } from "./MediaKitMetricsWindowCaption";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";

/**
 * Capture width 820px maps to 182mm on A4. Stay under the 251mm content
 * box so pages are never scaled (scaling recenters and shifts columns).
 */
const PDF_TABLE_WIDTH_PX = 820;
const PDF_TABLE_PAGE_HEIGHT_PX = 1100;
const TITLE_BLOCK_PX = 40;
const THEAD_PX = 32;
const ROW_PX = 48;

function rowsOnPage(): number {
  return Math.max(
    1,
    Math.floor(
      (PDF_TABLE_PAGE_HEIGHT_PX - TITLE_BLOCK_PX - 12 - THEAD_PX) / ROW_PX,
    ),
  );
}

function chunkVideos(
  videos: MediaKitYoutubeCatalogVideo[],
): MediaKitYoutubeCatalogVideo[][] {
  const chunks: MediaKitYoutubeCatalogVideo[][] = [];
  const n = rowsOnPage();
  for (let i = 0; i < videos.length; i += n) {
    chunks.push(videos.slice(i, i + n));
  }
  const last = chunks[chunks.length - 1];
  if (last && last.length >= n) {
    const overflow = last.pop();
    if (overflow) chunks.push([overflow]);
  }
  return chunks;
}

function catalogTotals(videos: MediaKitYoutubeCatalogVideo[]): {
  count: number;
  views: number;
  interactions: number;
  engagementRate: number | undefined;
} {
  const views = videos.reduce((sum, item) => sum + (item.views || 0), 0);
  const interactions = videos.reduce(
    (sum, item) => sum + (item.interactions || 0),
    0,
  );
  return {
    count: videos.length,
    views,
    interactions,
    engagementRate: catalogEngagementPercentFromConsumption({
      totalViews: views,
      totalInteractions: interactions,
    }),
  };
}

function formatTableDate(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return formatDate(value);
  const locale = i18n.language?.startsWith("en") ? "en-US" : "es-CL";
  return date.toLocaleDateString(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

const thStyle: React.CSSProperties = {
  fontFamily: '"DM Sans", sans-serif',
  fontSize: "0.7rem",
  fontWeight: 700,
  letterSpacing: "0.02em",
  textTransform: "uppercase",
  color: "#1a1a1a",
  backgroundColor: "#f4f4f6",
  borderBottom: "1px solid rgba(26, 25, 48, 0.14)",
  padding: "8px 8px",
  textAlign: "left",
  lineHeight: 1.2,
  whiteSpace: "nowrap",
  overflow: "hidden",
};

const tdStyle: React.CSSProperties = {
  fontFamily: '"DM Sans", sans-serif',
  fontSize: "0.75rem",
  color: "#1a1a1a",
  borderBottom: "1px solid rgba(26, 25, 48, 0.08)",
  padding: "6px 8px",
  verticalAlign: "middle",
  lineHeight: 1.25,
  overflow: "hidden",
};

const YoutubeCatalogPdfTable: React.FC<{
  videos: MediaKitYoutubeCatalogVideo[];
  totals?: {
    count: number;
    views: number;
    interactions: number;
    engagementRate: number | undefined;
  };
}> = ({ videos, totals }) => {
  const { t } = useTranslation("mediaKit");
  return (
  <table
    style={{
      width: PDF_TABLE_WIDTH_PX,
      maxWidth: PDF_TABLE_WIDTH_PX,
      borderCollapse: "collapse",
      tableLayout: "fixed",
    }}
  >
    <colgroup>
      <col style={{ width: 78 }} />
      <col />
      <col style={{ width: 100 }} />
      <col style={{ width: 72 }} />
      <col style={{ width: 100 }} />
      <col style={{ width: 84 }} />
      <col style={{ width: 52 }} />
    </colgroup>
    <thead>
      <tr style={{ height: THEAD_PX }}>
        <th style={{ ...thStyle, paddingLeft: 6 }} />
        <th style={thStyle}>{t("title")}</th>
        <th style={thStyle}>{t("date")}</th>
        <th style={{ ...thStyle, textAlign: "right" }}>Views</th>
        <th style={{ ...thStyle, textAlign: "right" }}>Int.</th>
        <th style={{ ...thStyle, textAlign: "right" }}>Eng.</th>
        <th style={thStyle}>Link</th>
      </tr>
    </thead>
    <tbody>
      {videos.map((item, rowIndex) => {
        const thumb = catalogThumbSrc(item);
        return (
          <tr
            key={item.id}
            style={{
              backgroundColor: rowIndex % 2 === 1 ? "#fafafa" : "#ffffff",
              height: ROW_PX,
            }}
          >
            <td style={{ ...tdStyle, padding: 6, width: 78 }}>
              <div
                data-mediakit-pdf-thumb-cover=""
                data-mediakit-pdf-keep-overflow=""
                style={{
                  width: 64,
                  height: 36,
                  borderRadius: 4,
                  overflow: "hidden",
                  backgroundColor: "#ececef",
                }}
              >
                {thumb ? (
                  <img
                    src={thumb}
                    alt=""
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                ) : null}
              </div>
            </td>
            <td
              style={{
                ...tdStyle,
                fontWeight: 600,
              }}
            >
              <div
                style={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  wordBreak: "break-word",
                }}
              >
                {item.title || t("untitled")}
              </div>
            </td>
            <td
              style={{
                ...tdStyle,
                color: "#555555",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
              }}
            >
              {formatTableDate(item.publishedAt)}
              {item.inProgress ? (
                <div
                  style={{
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    color: "#b26a00",
                    textTransform: "uppercase",
                  }}
                >
                  {t("inProgress")}
                </div>
              ) : null}
            </td>
            <td
              style={{
                ...tdStyle,
                textAlign: "right",
                fontVariantNumeric: "tabular-nums",
                whiteSpace: "nowrap",
              }}
            >
              {formatNumber(item.views)}
            </td>
            <td
              style={{
                ...tdStyle,
                textAlign: "right",
                fontVariantNumeric: "tabular-nums",
                whiteSpace: "nowrap",
              }}
            >
              {formatNumber(item.interactions)}
            </td>
            <td
              style={{
                ...tdStyle,
                textAlign: "right",
                fontVariantNumeric: "tabular-nums",
                whiteSpace: "nowrap",
              }}
            >
              {item.engagementRate != null
                ? `${item.engagementRate.toFixed(2)}%`
                : "—"}
            </td>
            <td style={{ ...tdStyle, padding: "6px 4px" }}>
              {item.url ? (
                <a
                  data-mediakit-pdf-link=""
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    width: "100%",
                    height: 28,
                    fontFamily: '"DM Sans", sans-serif',
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    color: "#1557c0",
                    textDecoration: "underline",
                    pointerEvents: "auto",
                  }}
                >
                  {t("view")}
                </a>
              ) : (
                "—"
              )}
            </td>
          </tr>
        );
      })}
    </tbody>
    {totals ? (
      <tfoot>
        <tr
          style={{
            height: ROW_PX,
            backgroundColor: "#f4f4f6",
          }}
        >
          <td
            colSpan={3}
            style={{
              ...tdStyle,
              fontWeight: 700,
              borderTop: "1px solid rgba(26, 25, 48, 0.18)",
              borderBottom: "none",
            }}
          >
            {t("total", { count: totals.count })}
          </td>
          <td
            style={{
              ...tdStyle,
              textAlign: "right",
              fontWeight: 700,
              fontVariantNumeric: "tabular-nums",
              whiteSpace: "nowrap",
              borderTop: "1px solid rgba(26, 25, 48, 0.18)",
              borderBottom: "none",
            }}
          >
            {formatNumber(totals.views)}
          </td>
          <td
            style={{
              ...tdStyle,
              textAlign: "right",
              fontWeight: 700,
              fontVariantNumeric: "tabular-nums",
              whiteSpace: "nowrap",
              borderTop: "1px solid rgba(26, 25, 48, 0.18)",
              borderBottom: "none",
            }}
          >
            {formatNumber(totals.interactions)}
          </td>
          <td
            style={{
              ...tdStyle,
              textAlign: "right",
              fontWeight: 700,
              fontVariantNumeric: "tabular-nums",
              whiteSpace: "nowrap",
              borderTop: "1px solid rgba(26, 25, 48, 0.18)",
              borderBottom: "none",
            }}
          >
            {totals.engagementRate != null
              ? `${totals.engagementRate.toFixed(2)}%`
              : "—"}
          </td>
          <td
            style={{
              ...tdStyle,
              borderTop: "1px solid rgba(26, 25, 48, 0.18)",
              borderBottom: "none",
            }}
          />
        </tr>
      </tfoot>
    ) : null}
  </table>
  );
};

export const MediaKitYoutubeCatalogPdfAppendix: React.FC<{
  videos: MediaKitYoutubeCatalogVideo[];
}> = ({ videos }) => {
  const { t } = useTranslation("mediaKit");
  const metricsWindow = useMediaKitMetricsWindow();
  if (videos.length === 0) return null;

  const chunks = chunkVideos(videos);
  const totals = catalogTotals(videos);

  return (
    <Box
      aria-hidden
      data-mediakit-pdf-only=""
      sx={{
        position: "fixed",
        left: -10000,
        top: 0,
        width: PDF_TABLE_WIDTH_PX,
        pointerEvents: "none",
      }}
    >
      {chunks.map((chunk, index) => (
        <Box
          key={index}
          component="section"
          data-mediakit-pdf-section=""
          data-mediakit-pdf-fill-page=""
          sx={{
            width: PDF_TABLE_WIDTH_PX,
            minWidth: PDF_TABLE_WIDTH_PX,
            maxWidth: PDF_TABLE_WIDTH_PX,
            height: PDF_TABLE_PAGE_HEIGHT_PX,
            minHeight: PDF_TABLE_PAGE_HEIGHT_PX,
            maxHeight: PDF_TABLE_PAGE_HEIGHT_PX,
            boxSizing: "border-box",
            overflow: "hidden",
          }}
        >
          <CardHeading
            variant="h6"
            sx={{
              fontWeight: 700,
              mb: 1.5,
              lineHeight: 1.2,
              height: TITLE_BLOCK_PX - 12,
              overflow: "hidden",
            }}
          >
            {t("pdfVideosTitle")}
            {metricsWindow ? (
              <Box
                component="span"
                sx={{ fontSize: "0.75rem", fontWeight: 500, ml: 1 }}
              >
                {t("pdfMetricsAtDay", { days: metricsWindow.longFormDays })}
              </Box>
            ) : null}
          </CardHeading>
          <YoutubeCatalogPdfTable
            videos={chunk}
            totals={index === chunks.length - 1 ? totals : undefined}
          />
        </Box>
      ))}
    </Box>
  );
};
